import { useSelector } from "react-redux";
import NavImage from "./NavImage";
import { Col, Row } from "../../../common/components/Layout";
import { DesktopNavigationList, NavigationOverview } from "./NavigationList";
import {
  selectDesktopVisibleParentNodes,
  selectNavigationBigImage,
  selectNavigationDisplayMode,
  selectNavigationMenuLevel,
} from "../../../Header/redux/navigationSelectors";
import { GoBackLink } from "./GoBackLink";
import { NavigationSeriesView } from "./NavigationSeriesView";
import { type MenuProps } from "../../../common/components/Menu/Menu";
import { type CSSProperties, useEffect, useState } from "react";
import { SearchDisplayMode } from "~/Search/models";

import classes from "./NavigationFlyout.module.less";
import { Dialog } from "~/common/m3/Dialog/Dialog";

interface Props extends MenuProps {
  onClose: () => void;
}

export const NavigationFlyout = ({ open, onClose }: Props) => {
  const [offsetStyle, setOffsetStyle] = useState<CSSProperties | undefined>();

  useEffect(() => {
    if (open) {
      const offset = Array.from(window.document.querySelectorAll(".js-header-offset"))
        .map((el) => el.getBoundingClientRect()?.height ?? 0)
        .reduce((a, b) => a + b, 0);

      const negativeOffset = Array.from(window.document.querySelectorAll(".js-header-negative-offset"))
        .map((el) => el.getBoundingClientRect()?.height ?? 0)
        .reduce((a, b) => a + b, 0);

      setOffsetStyle({
        top: offset - negativeOffset - window.scrollY,
      });
    }
  }, [open]);

  return (
    <Dialog
      className={classes.root}
      open={open}
      onClose={onClose}
      slotProps={{ backdrop: { style: offsetStyle }, root: { style: offsetStyle } }}
      PaperProps={{ elevation: 0 }}
    >
      <NavigationFlyoutContent />
    </Dialog>
  );
};

const NavigationFlyoutContent = () => {
  const image = useSelector(selectNavigationBigImage);
  const navigationMenuLevel = useSelector(selectNavigationMenuLevel);

  const isOpen = navigationMenuLevel > 0;
  const showBack = navigationMenuLevel > 2;
  const transformVal = navigationMenuLevel > 1 ? `translateX(-${(navigationMenuLevel - 2) * 50}%)` : undefined;

  const nodesToShow = useSelector(selectDesktopVisibleParentNodes);

  const navigationColumns = nodesToShow.map(({ curr, prev }, index) => (
    <Col key={index} size={6} className={"flex-none scha__fade-in-out"}>
      {[
        curr && <DesktopNavigationList key={curr.id} parentNode={curr} active={true} />,
        prev && prev !== curr && <DesktopNavigationList key={prev.id} parentNode={prev} active={false} />,
      ]}
    </Col>
  ));

  const displayMode = useSelector(selectNavigationDisplayMode);

  return (
    <div className="scha__sub-nav" hidden={!isOpen}>
      <div className="container">
        {displayMode === SearchDisplayMode.LIST && (
          <>
            <GoBackLink className="mb-3" hidden={!showBack} visibleColAmount={2} />
            <Row>
              <Col size={8} style={{ overflow: "hidden" }}>
                <Row className={"flex-wrap-none"} style={{ transition: "transform 600ms", transform: transformVal }}>
                  {navigationColumns}
                </Row>
              </Col>
              <Col size={4}>{image && <NavImage imageData={image} />}</Col>
            </Row>
          </>
        )}
        {displayMode === SearchDisplayMode.SERIES_TREE && (
          <Row>
            <Col size={6}>
              <NavigationOverview node={nodesToShow[0].curr!} navigationLevel={nodesToShow[0].curr!.level} />
              <NavigationSeriesView className={"scha__nav__series-table"} />
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};
